var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ElementRef, OnDestroy, OnInit, QueryList, } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@env/environment';
import { ScrollContentItemDirective } from '@features/home/home/directives/scroll-content-item.directive';
import { ScrollMenuItemDirective } from '@features/home/home/directives/scroll-menu-item.directive';
import { Metas } from '@features/home/home/seo-vars';
import { LocaleEnum } from '@models/commons/locales.enum';
import { Select, Store } from '@ngxs/store';
import { CompaniesState } from '@store/companies/companies.state';
import { FindJobs, InitStaticData } from '@store/jobs/jobs.actions';
import { JobsState, NB_ITEMS_TO_DISPLAY } from '@store/jobs/jobs.state';
import { combineLatest, fromEvent, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, takeUntil, tap, throttleTime, } from 'rxjs/operators';
export class HomeComponent {
    constructor(breakpointObserver, window, platformId, localeId, store, metaService, route) {
        this.breakpointObserver = breakpointObserver;
        this.window = window;
        this.platformId = platformId;
        this.localeId = localeId;
        this.store = store;
        this.metaService = metaService;
        this.route = route;
        this.menuItems = [];
        this.contentItems = [];
        this.nbItemsToDisplay = NB_ITEMS_TO_DISPLAY;
        this.destroyed$ = new Subject();
        this.metaElements = [];
        this.currentPage$ = route.queryParamMap.pipe(map(filterMap => (filterMap.has('page') ? Number(filterMap.get('page')) : 1)), distinctUntilChanged(), shareReplay());
        this.jobs$ = combineLatest([
            this.currentPage$,
            this.company$.pipe(filter(company => !!company), map(company => company._id), distinctUntilChanged()),
        ]).pipe(tap(([_, companyId]) => store.dispatch(new FindJobs(companyId))), switchMap(([page, companyId]) => store.select(JobsState.list(companyId, page))), shareReplay());
    }
    ngOnInit() {
        this.store.dispatch(new InitStaticData());
        this.company$.pipe(filter(company => !!company)).subscribe(company => {
            this.cleanSeo();
            this.metaElements = [
                this.metaService.addTag({
                    name: 'author',
                    content: company.name,
                }),
                this.metaService.addTag({
                    itemprop: 'name',
                    content: company.name,
                }),
                this.metaService.addTag({
                    property: 'og:url',
                    content: `https://${company._id}.atsii.fr`,
                }),
                this.metaService.addTag({
                    property: 'og:image',
                    content: `${environment.api.googleStorage}/${environment.wizbiiFiles}/${company.logo}`,
                }),
                this.metaService.addTag({
                    property: 'og:site_name',
                    content: `${company._id}.atsii.fr`,
                }),
                this.metaService.addTag({
                    property: 'og:locale',
                    content: LocaleEnum[this.localeId],
                }),
                this.metaService.addTag({
                    property: 'profile:username',
                    content: company.name,
                }),
                this.metaService.addTag({
                    itemprop: 'image',
                    content: `${environment.api.googleStorage}/${environment.wizbiiFiles}/${company.logo}`,
                }),
                ...this.metaService.addTags(Metas),
            ];
        });
    }
    ngAfterViewInit() {
        this.initBreakpointObserver();
    }
    initBreakpointObserver() {
        if (!isPlatformBrowser(this.platformId) || !this.navBar || !this.content || !this.menuItems) {
            return;
        }
        this.contentItems = this.content.toArray(); // https://github.com/angular/angular/issues/17414
        this.menuItems = this.menu.toArray(); // https://github.com/angular/angular/issues/17414
        this.breakpointObserver
            .observe('(min-width: .0625em)')
            .pipe(takeUntil(this.destroyed$))
            .subscribe(event => {
            if (event.matches) {
                this.scrollSub = fromEvent(this.window, 'scroll', { passive: true })
                    .pipe(takeUntil(this.destroyed$), startWith(null), throttleTime(50, undefined, { leading: true, trailing: true }))
                    .subscribe(() => {
                    this.updateMenuItems();
                });
            }
            else if (this.scrollSub) {
                this.scrollSub.unsubscribe();
            }
        });
    }
    cleanSeo() {
        this.metaElements.forEach(meta => {
            this.metaService.removeTagElement(meta);
        });
        this.metaElements = [];
    }
    updateMenuItems() {
        // Get the position of the center of the `navBar box` from the top
        const navBarPos = this.navBar.nativeElement.getBoundingClientRect().bottom - this.navBar.nativeElement.clientHeight / 2;
        const closestItem = this.contentItems.reduce((closest, next) => {
            // Compare the previous bottom value to the next top value
            return Math.abs(closest.rect.bottom - navBarPos) > Math.abs(next.rect.top - navBarPos) ? next : closest;
        }, this.contentItems[0]);
        // Add the active class to the menu-item who have the same index as the closest content-item
        this.menuItems.forEach((item, index) => {
            item.toggleClass(closestItem === this.contentItems[index]);
        });
    }
    trackByMedia(_, media) {
        return media.name;
    }
    ngOnDestroy() {
        this.cleanSeo();
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
__decorate([
    Select(CompaniesState.authenticatedCompany),
    __metadata("design:type", Observable)
], HomeComponent.prototype, "company$", void 0);
